import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { operations as docServiceOperations } from 'store/docServiceManager'
import { operations as lmsOperations } from 'store/lmsManager'
import { HS_SITE_REPORTS_LESSON_PAGE_FLAG, isFlagOn } from 'utils/featureFlags'
import AdminExportDownloadButton from './component'

const AdminExportDownloadButtonContainer = ({
  className,
  clearDocumentUrl,
  documentUrl,
  fetchDocumentUrl,
  fetchHsLeaderHistoricalReports,
  fetchHsSiteHistoricalReports,
  fetchK8LeaderHistoricalReports,
  isFetchingCurrentYear,
  programKey,
  selectedProgram,
  siteId,
  tooltipLocation,
  useLeaderToolsStyles,
  user,
  usersHistoricalUserReports,
  buttonText,
}) => {
  const useHsSiteReports = isFlagOn([HS_SITE_REPORTS_LESSON_PAGE_FLAG])
  const hsProgramSelected = programKey?.includes('HS')

  const [
    isDownloadingHistoricalUserReports,
    setDownloadingHistoricalUserReports,
  ] = useState(false)
  useEffect(() => {
    if (documentUrl) {
      // download current year document
      const downloadLink = document.createElement('a')
      downloadLink.href = documentUrl
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      clearDocumentUrl()
    }
  }, [documentUrl, clearDocumentUrl])

  const handleClickCurrentYear = useCallback(() => {
    if (!isFetchingCurrentYear && !documentUrl) {
      if (useHsSiteReports && hsProgramSelected && siteId) {
        fetchDocumentUrl({ siteId, program: selectedProgram })
      } else {
        fetchDocumentUrl({ userEmail: user?.email, program: selectedProgram })
      }
    }
    return true
  }, [
    documentUrl,
    fetchDocumentUrl,
    hsProgramSelected,
    isFetchingCurrentYear,
    selectedProgram,
    siteId,
    useHsSiteReports,
    user?.email,
  ])

  useEffect(() => {
    if (!programKey) return

    if (programKey.includes('K8') && user?.id) {
      fetchK8LeaderHistoricalReports(user.id)
    }

    if (hsProgramSelected) {
      if (useHsSiteReports && siteId) {
        fetchHsSiteHistoricalReports(siteId)
      } else if (user?.id) {
        fetchHsLeaderHistoricalReports(user.id)
      }
    }
  }, [
    user?.id,
    programKey,
    siteId,
    hsProgramSelected,
    useHsSiteReports,
    fetchK8LeaderHistoricalReports,
    fetchHsLeaderHistoricalReports,
    fetchHsSiteHistoricalReports,
  ])

  const historicalReportingOnClick = async (data, filename, url) => {
    if (!url && !data) {
      return false
    }

    if (!data) {
      try {
        // fetch from the url so we can change the name of the file
        setDownloadingHistoricalUserReports(true)
        const response = await fetch(url)
        const blob = await response.blob()
        const newUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = newUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // done
        window.URL.revokeObjectURL(newUrl)
        setDownloadingHistoricalUserReports(false)

        return true
      } catch (error) {
        console.error('Error downloading file:', error)
        return false
      }
    }

    return { data, filename }
  }
  return (
    <AdminExportDownloadButton
      buttonText={buttonText}
      className={className}
      historicalReportingOnClick={historicalReportingOnClick}
      historicalReports={usersHistoricalUserReports}
      isFetching={isFetchingCurrentYear || isDownloadingHistoricalUserReports}
      onClickCurrentYear={handleClickCurrentYear}
      programKey={programKey}
      tooltipLocation={tooltipLocation}
      useLeaderToolsStyles={useLeaderToolsStyles}
    />
  )
}

const mapStateToProps = (state, ownProps) => {
  const { key: selectedProgramKey } = state.reportsManager.selectedProgram || {}
  const { historicalUserReports } = state.lmsManager

  return {
    documentUrl: state.docServiceManager.documentUrl,
    isFetchingCurrentYear: state.docServiceManager.isFetching,
    user: state.userProfileManager?.profile,
    selectedProgram: selectedProgramKey,
    siteId: ownProps.siteId ?? null,
    usersHistoricalUserReports:
      historicalUserReports?.[selectedProgramKey] || [],
  }
}

const mapDispatchToProps = {
  clearDocumentUrl: docServiceOperations.clearDocumentUrl,
  fetchDocumentUrl: docServiceOperations.fetchDocumentUrl,
  fetchK8LeaderHistoricalReports: lmsOperations.fetchK8LeaderHistoricalReports,
  fetchHsLeaderHistoricalReports: lmsOperations.fetchHsLeaderHistoricalReports,
  fetchHsSiteHistoricalReports: lmsOperations.fetchHsSiteHistoricalReports,
}

AdminExportDownloadButtonContainer.propTypes = {
  buttonText: PropTypes.string,
  className: PropTypes.string,
  clearDocumentUrl: PropTypes.func,
  documentUrl: PropTypes.string,
  fetchDocumentUrl: PropTypes.func,
  fetchHsLeaderHistoricalReports: PropTypes.func,
  fetchHsSiteHistoricalReports: PropTypes.func,
  fetchK8LeaderHistoricalReports: PropTypes.func,
  isFetchingCurrentYear: PropTypes.bool,
  isFetchingHistoricalUserReports: PropTypes.bool,
  programKey: PropTypes.string,
  selectedProgram: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltipLocation: PropTypes.string,
  useLeaderToolsStyles: PropTypes.bool,
  user: PropTypes.object,
  usersHistoricalUserReports: PropTypes.array,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AdminExportDownloadButtonContainer)
